import { Editor } from "@tiptap/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Quote,
  Image,
  Link,
  Table,
  CheckSquare,
  CodeSquare,
  Minus,
  ChevronDown,
  RowsIcon,
  ColumnsIcon,
  Trash2,
  Type,
} from "lucide-react";

interface EditorToolbarProps {
  editor: Editor | null;
}

const EditorToolbar = ({ editor }: EditorToolbarProps) => {
  if (!editor) {
    return null;
  }

  const insertTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  const addColumnBefore = () => {
    editor.chain().focus().addColumnBefore().run();
  };

  const addColumnAfter = () => {
    editor.chain().focus().addColumnAfter().run();
  };

  const deleteColumn = () => {
    editor.chain().focus().deleteColumn().run();
  };

  const addRowBefore = () => {
    editor.chain().focus().addRowBefore().run();
  };

  const addRowAfter = () => {
    editor.chain().focus().addRowAfter().run();
  };

  const deleteRow = () => {
    editor.chain().focus().deleteRow().run();
  };

  const insertImage = () => {
    const url = window.prompt("输入图片 URL");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const insertLink = () => {
    const url = window.prompt("输入链接 URL");
    if (url) {
      if (editor.isActive("link")) {
        editor.chain().focus().unsetLink().run();
      }
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const toolbarButtons = [
    {
      type: "group",
      icon: <Heading1 className="w-4 h-4" data-icon="Heading1" />,
      label: "标题",
      items: [
        {
          icon: <Heading1 className="w-4 h-4" data-icon="Heading1" />,
          label: "一级标题",
          isActive: () => editor.isActive("heading", { level: 1 }),
          action: () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
        },
        {
          icon: <Heading2 className="w-4 h-4" data-icon="Heading2" />,
          label: "二级标题",
          isActive: () => editor.isActive("heading", { level: 2 }),
          action: () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
        },
        {
          icon: <Heading3 className="w-4 h-4" data-icon="Heading3" />,
          label: "三级标题",
          isActive: () => editor.isActive("heading", { level: 3 }),
          action: () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run(),
        },
        {
          icon: <Heading4 className="w-4 h-4" data-icon="Heading4" />,
          label: "四级标题",
          isActive: () => editor.isActive("heading", { level: 4 }),
          action: () =>
            editor.chain().focus().toggleHeading({ level: 4 }).run(),
        },
        {
          icon: <Heading5 className="w-4 h-4" data-icon="Heading5" />,
          label: "五级标题",
          isActive: () => editor.isActive("heading", { level: 5 }),
          action: () =>
            editor.chain().focus().toggleHeading({ level: 5 }).run(),
        },
      ],
    },
    {
      icon: <Type className="w-4 h-4" />,
      label: "正文",
      action: () => editor.chain().focus().setParagraph().run(),
    },
    { type: "separator" },
    {
      icon: <Bold className="w-4 h-4" />,
      isActive: editor.isActive("bold"),
      action: () => editor.chain().focus().toggleBold().run(),
      label: "粗体",
    },
    {
      icon: <Italic className="w-4 h-4" />,
      isActive: editor.isActive("italic"),
      action: () => editor.chain().focus().toggleItalic().run(),
      label: "斜体",
    },
    {
      icon: <Code className="w-4 h-4" />,
      isActive: editor.isActive("code"),
      action: () => editor.chain().focus().toggleCode().run(),
      label: "行内代码",
    },
    { type: "separator" },
    {
      icon: <List className="w-4 h-4" />,
      isActive: editor.isActive("bulletList"),
      action: () => editor.chain().focus().toggleBulletList().run(),
      label: "无序列表",
    },
    {
      icon: <ListOrdered className="w-4 h-4" />,
      isActive: editor.isActive("orderedList"),
      action: () => editor.chain().focus().toggleOrderedList().run(),
      label: "有序列表",
    },
    {
      icon: <CheckSquare className="w-4 h-4" />,
      isActive: editor.isActive("taskList"),
      action: () => editor.chain().focus().toggleTaskList().run(),
      label: "任务列表",
    },
    { type: "separator" },
    {
      icon: <Quote className="w-4 h-4" />,
      isActive: editor.isActive("blockquote"),
      action: () => editor.chain().focus().toggleBlockquote().run(),
      label: "引用",
    },
    {
      icon: <CodeSquare className="w-4 h-4" />,
      isActive: editor.isActive("codeBlock"),
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      label: "代码块",
    },
    { type: "separator" },
    {
      icon: <Link className="w-4 h-4" />,
      action: insertLink,
      label: "插入链接",
    },
    {
      icon: <Image className="w-4 h-4" />,
      action: insertImage,
      label: "插入图片",
    },
    {
      icon: <Minus className="w-4 h-4" />,
      action: () => editor.chain().focus().setHorizontalRule().run(),
      label: "插入分割线",
    },
    { type: "separator" },
    {
      type: "group",
      icon: <Table className="w-4 h-4" />,
      label: "表格工具",
      items: [
        {
          icon: <Table className="w-4 h-4" />,
          label: "插入表格",
          action: insertTable,
        },
        { type: "separator" },
        {
          icon: <RowsIcon className="w-4 h-4" />,
          label: "在上方插入行",
          action: addRowBefore,
        },
        {
          icon: <RowsIcon className="w-4 h-4" />,
          label: "在下方插入行",
          action: addRowAfter,
        },
        {
          icon: <Trash2 className="w-4 h-4" />,
          label: "删除行",
          action: deleteRow,
        },
        { type: "separator" },
        {
          icon: <ColumnsIcon className="w-4 h-4" />,
          label: "在左侧插入列",
          action: addColumnBefore,
        },
        {
          icon: <ColumnsIcon className="w-4 h-4" />,
          label: "在右侧插入列",
          action: addColumnAfter,
        },
        {
          icon: <Trash2 className="w-4 h-4" />,
          label: "删除列",
          action: deleteColumn,
        },
      ],
    },
  ];

  return (
    <div className="editor-toolbar p-1 mb-0">
      <div className="flex items-center flex-row flex-wrap gap-1">
        {toolbarButtons.map((button, index) => {
          if (button.type === "separator") {
            return (
              <div
                key={`sep-${index}`}
                className="editor-toolbar-separator w-px h-4 mx-1"
              ></div>
            );
          }

          if (button.type === "group") {
            return (
              <div key={button.label} className="toolbar-button-group">
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      className={`toolbar-button with-dropdown flex items-center gap-1 ${
                        button.items &&
                        button.items.some(
                          (item) =>
                            "isActive" in item &&
                            item.isActive &&
                            item.isActive()
                        )
                          ? "active"
                          : ""
                      }`}
                      aria-label={button.label}
                      title={button.label}
                    >
                      {button.icon}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content className="toolbar-dropdown-content">
                      {button.items &&
                        button.items.map((item, itemIndex) => {
                          if ("type" in item && item.type === "separator") {
                            return (
                              <DropdownMenu.Separator
                                key={`sep-${itemIndex}`}
                                className="toolbar-dropdown-separator"
                              />
                            );
                          }

                          return (
                            <DropdownMenu.Item
                              key={item.label}
                              className={`toolbar-dropdown-item ${
                                "isActive" in item &&
                                item.isActive &&
                                item.isActive()
                                  ? "active"
                                  : ""
                              }`}
                              onClick={item.action}
                            >
                              {item.icon}
                              <span>{item.label}</span>
                            </DropdownMenu.Item>
                          );
                        })}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
            );
          }

          return (
            <button
              key={button.label}
              className={`toolbar-button ${button.isActive ? "active" : ""}`}
              onClick={button.action}
              aria-label={button.label}
              title={button.label}
            >
              {button.icon}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EditorToolbar;
